import IcImage from '@/assets/icons/ic-image.svg';
import { cn } from '@/lib';
import { formatCurrency } from '@/lib/currency';
import type { FunnelPageBranchTag } from '../../../logic';
import type { FunnelPageProductInfo } from '../../../types';

interface FunnelPageNavRowProps {
    productInfo: FunnelPageProductInfo | undefined;
    fallbackTitle: string;
    subtitle?: string;
    branchTag?: FunnelPageBranchTag;
    parentStepNumber?: number;
    isHidden?: boolean;
    isAncestorHidden?: boolean;
    isNew?: boolean;
    active: boolean;
    onClick: () => void;
    stepNumber?: number;
}

export const FunnelPageNavRow = ({
    productInfo,
    fallbackTitle,
    subtitle,
    branchTag,
    parentStepNumber,
    isHidden = false,
    isAncestorHidden = false,
    isNew = false,
    active,
    onClick,
    stepNumber,
}: FunnelPageNavRowProps) => {
    const title = productInfo?.title ?? fallbackTitle;
    const priceLabel =
        typeof productInfo?.price === 'number' && productInfo.price > 0
            ? formatCurrency(productInfo.price, productInfo.currency ?? 'USD')
            : '';

    return (
        <button
            type='button'
            onClick={onClick}
            aria-current={active ? 'step' : undefined}
            className={cn(
                'group flex min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left transition-all duration-150',
                active ? 'bg-brand-subtle' : 'hover:bg-subtle hover:translate-x-0.5',
                (isHidden || isAncestorHidden) && 'opacity-60',
                isNew && 'animate-bounce-in'
            )}
        >
            {stepNumber !== undefined && (
                <span className='text-caption-2-bold text-gray w-4 shrink-0 text-center tabular-nums'>
                    {stepNumber}
                </span>
            )}

            <div
                className={cn(
                    'bg-canvas text-brand-main text-caption-1-bold flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border transition-all',
                    isHidden && 'border-dashed',
                    active ? 'border-brand-main ring-brand-selected ring-2' : 'border-neutral-subtle'
                )}
            >
                {productInfo?.image ? (
                    <img src={productInfo.image} alt='' className='size-full object-cover' />
                ) : (
                    <IcImage className='text-gray size-5' />
                )}
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <div className='flex min-w-0 items-center gap-1.5'>
                    {isHidden ? (
                        <span className='bg-neutral-subtle text-gray text-caption-2-bold shrink-0 rounded px-1.5 py-px uppercase'>
                            Hidden
                        </span>
                    ) : branchTag ? (
                        <>
                            <span className={cn('text-label-xs-bold! font-extrabold uppercase', branchTag.colorClass)}>
                                {branchTag.label}
                            </span>
                            {parentStepNumber !== undefined && (
                                <span className='text-caption-2-bold text-gray group-hover:text-dark shrink-0 -translate-x-0.5 transition-colors'>
                                    from Page {parentStepNumber}
                                </span>
                            )}
                        </>
                    ) : subtitle ? (
                        <span className='text-caption-2-bold text-gray truncate uppercase'>{subtitle}</span>
                    ) : null}
                </div>
                <span className='text-heading-h5 text-dark truncate'>{title}</span>
            </div>
            <span className='text-label-sm-bold text-dark ml-auto shrink-0 pl-1'>{priceLabel}</span>
        </button>
    );
};
